/**
 * One reviewable row in the connection-import dialog: checkbox, alias +
 * endpoint (and, for a SQL Server `Encrypt=Strict` source, how Jam honours it),
 * an auth badge, optional Windows-auth-unavailable warning (with tooltip), an
 * "already added" duplicate badge, and an optional inline password field
 * (prefilled when a documented plaintext file supplied one).
 */
import { AlertTriangle } from 'lucide-react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ConnectionEngineIcon } from '../components/ConnectionEngineIcon';
import { isComingSoonProvider } from './connectionImportUtils';
import type { ImportCandidate } from '../../../types/connectionImport';
import { safeConnectionTarget, safeServerText } from '../../../types/safeConnectionDisplay';
import { ENCRYPT_STRICT_NOTE } from '../../../core/engines/mssqlEncrypt';

function osLabel(): string {
  if (typeof navigator === 'undefined') return 'this OS';
  const platform = navigator.platform || '';
  if (/mac/i.test(platform)) return 'macOS';
  if (/linux/i.test(platform)) return 'Linux';
  return 'this OS';
}

/** Auth types that accept an inline password during review. */
function acceptsPassword(candidate: ImportCandidate): boolean {
  return (
    candidate.supported &&
    (candidate.authentication === 'SQL Server authentication' ||
      candidate.authentication === 'Password')
  );
}

interface ImportCandidateRowProps {
  candidate: ImportCandidate;
  checked: boolean;
  duplicate: boolean;
  password: string;
  onToggle: (checked: boolean) => void;
  onPasswordChange: (password: string) => void;
}

export function ImportCandidateRow({
  candidate,
  checked,
  duplicate,
  password,
  onToggle,
  onPasswordChange,
}: ImportCandidateRowProps) {
  const tns = candidate.oracleConnectionStringType === 'tns_descriptor' || candidate.oracleConnectionStringType === 'tns_alias';
  const endpoint = [
    // A candidate read from another tool's settings can carry a secret in its server text (security slice); an Oracle
    // TNS candidate is shown by what it dials (its alias or described descriptor), not a server it may not have.
    tns
      ? safeConnectionTarget({ ...candidate, engine: 'oracle' })
      : candidate.port && candidate.port !== '<auto>'
        ? `${safeServerText(candidate.server, candidate.engine)}:${candidate.port}`
        : safeServerText(candidate.server, candidate.engine),
    candidate.database,
  ]
    .filter(Boolean)
    .join(' · ');

  const authText = candidate.user
    ? `${candidate.authLabel} · ${candidate.user}`
    : candidate.authLabel;

  return (
    <div
      className={`flex items-center gap-3 rounded-md px-2 py-2 ${
        candidate.supported ? 'hover:bg-muted/50' : 'opacity-50'
      }`}
      data-testid={`import-row-${candidate.key}`}
    >
      <Checkbox
        checked={checked}
        disabled={!candidate.supported}
        onCheckedChange={(value) => onToggle(value === true)}
        data-testid={`import-row-checkbox-${candidate.key}`}
        aria-label={`Import ${safeServerText(candidate.alias)}`}
      />

      <ConnectionEngineIcon engine={candidate.engine || 'mssql'} size="sm" />

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{safeServerText(candidate.alias)}</div>
        <div className="truncate text-xs text-muted-foreground">{endpoint || '—'}</div>
        {candidate.encryptStrict && (
          <div className="text-xs text-muted-foreground" data-testid={`import-row-encrypt-strict-${candidate.key}`}>
            {ENCRYPT_STRICT_NOTE}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {!candidate.supported ? (
          isComingSoonProvider(candidate.providerName) ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-[10px]">
                  {candidate.providerName || 'This engine'} · coming soon
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                Importing {candidate.providerName || 'this engine'}&apos;s connections
                isn&apos;t available yet — it&apos;s on our roadmap.
              </TooltipContent>
            </Tooltip>
          ) : candidate.unsupportedReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-[10px]">
                  {candidate.providerName || 'Unsupported'} · not supported
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                {candidate.unsupportedReason}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Badge variant="outline" className="text-[10px]">
              {candidate.providerName || 'Unsupported'} · not supported
            </Badge>
          )
        ) : (
          <Badge variant="secondary" className="text-[10px]">
            {authText}
          </Badge>
        )}

        {duplicate && (
          <Badge variant="outline" className="text-[10px]" data-testid={`import-row-duplicate-${candidate.key}`}>
            already added
          </Badge>
        )}

        {candidate.windowsAuthUnavailable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="gap-1 border-amber-500/40 text-[10px] text-warning"
                data-testid={`import-row-winauth-${candidate.key}`}
              >
                <AlertTriangle className="h-3 w-3" />
                Windows auth
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px]">
              Windows authentication is not available on {osLabel()}. After importing, switch this
              connection to SQL login or Microsoft Entra ID before connecting.
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {acceptsPassword(candidate) && checked && !duplicate && (
        <Input
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Password…"
          className="h-7 w-28 shrink-0 text-xs"
          data-testid={`import-row-password-${candidate.key}`}
          aria-label={`Password for ${safeServerText(candidate.alias)}`}
        />
      )}
    </div>
  );
}
